import React, { useState, useEffect, useRef } from "react";
import { IoMdAnalytics } from "react-icons/io";
import { ImStatsDots } from "react-icons/im";
import { useAppLibs } from "../../ContextProvider/AppLib_Provider";
import Utils from "../../utils/utils";
import RadarChart from "./RadarChart ";
import LineChart from "./LineChart";
import Card from "./Card";
import CircularProgress from "./CircularProgress";

const StudentDashboard = ({ student }) => {
  const { Students } = useAppLibs();
  const [progressData, setProgressData] = useState([]);
  const [assessments, setAssessments] = useState(progressData);
  const STUDENT_CRITERIA = Students.STUDENT_CRITERIA;
  const assessmentCriteria = Object.keys(STUDENT_CRITERIA);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(0);

  // const currentRecord = assessments[selectedMonth];
  const [overallScore, setOverallScore] = useState(null);
  const scoreRef = useRef(null);

  const handleMonthSelect = (index) => {
    if (assessments[index]?.enabled) {
      setSelectedMonth(index);
    }
  };
  const enabledAssessments = progressData.filter((d) => d.enabled);
  const completedAssessments = enabledAssessments.filter(
    (d) => Object.keys(d.assessment).length > 0
  );
  const lastAssessment = completedAssessments.at(-1);
  const lastScore = lastAssessment
    ? (
        (Object.values(lastAssessment.assessment).reduce((a, b) => a + b, 0) /
          (Object.values(lastAssessment.assessment).length * 10)) *
        100
      ).toFixed(1) + "%"
    : "N/A";

  // Profile Score (Overall Average in Percentage)
  const allScores = completedAssessments.flatMap((d) =>
    Object.values(d.assessment)
  );

  const currentRecord = assessments[selectedMonth] || {}; // Ensure it doesn't break
  const currentAssessment = currentRecord.assessment || {}; // Default to empty object

  const currentOverallScore =
    Object.keys(currentAssessment).length > 0
      ? (
          (Object.values(currentAssessment).reduce(
            (acc, item) => acc + item,
            0
          ) /
            (Object.values(currentAssessment).length * 10)) *
          100
        ).toFixed(1) + "%"
      : "N/A"; // Return "N/A" if there's no assessment data

  const profileScore =
    allScores.length > 0
      ? (
          (allScores.reduce((a, b) => a + b, 0) / (allScores.length * 10)) *
          100
        ).toFixed(1) + "%"
      : "N/A";

  // Pending Assessments
  const pendingAssessments =
    enabledAssessments.length - completedAssessments.length;

  const fetchStudentProgress = async (student) => {
    if (!student._id) return;
    const result = await Students.getStudentProgressData({
      id: student._id,
      createdAt: student.createdAt,
    });
    if (result.success) {
      const data = result.data;
      setProgressData(data);
      setAssessments(data);
      calculateOverallScores(data);
      setLoading(false);
    } else {
      console.error("Error:", result.error);
    }
  };

  const calculateOverallScores = () => {
    const ret = progressData.reduce((acc, { month, assessment }) => {
      const values = Object.values(assessment);
      const m = Utils.formatDateTime(month);
      acc[m] =
        values.length > 0
          ? values.reduce((sum, val) => sum + val, 0) / values.length
          : 0;
      return acc;
    }, {});
    setOverallScore(ret);
  };

  useEffect(() => {
    calculateOverallScores();
  }, [progressData]);

  useEffect(() => {
    fetchStudentProgress(student);
  }, [student]);

  if (loading) return "Loading Dashboard...";

  return (
    <div className="bg-white shadow-md rounded-lg p-6 xs:p-4 w-full space-y-4">
      <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <ImStatsDots /> Your Progress
      </h2>
      <p className="text-gray-600">
        Monitor your performance and track progress through assessments &
        analytics.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-100 rounded-lg shadow-md">
        <Card title="Last Score" value={lastScore} color="bg-blue-500" />
        <Card title="Profile Score" value={profileScore} color="bg-green-500" />
        <CircularProgress pending={pendingAssessments} total={8} />
        <Card
          title="Total Assessments"
          value={completedAssessments.length}
          color="bg-purple-500"
        />
      </div>

      <div className="flex space-x-2 overflow-x-auto pb-2">
        {assessments.map((item, index) => (
          <button
            key={index}
            onClick={() => handleMonthSelect(index)}
            title={
              selectedMonth !== index && item.enabled
                ? "Click to track progress"
                : "Not enabled yet"
            }
            disabled={!item.enabled}
            className={`text-sm px-4 py-1.5 text-nowrap rounded-lg transition-colors ${
              selectedMonth === index && item.enabled
                ? "bg-blue-500 text-white"
                : item.enabled
                ? "bg-blue-100 hover:bg-blue-200"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
          >
            {Utils.formatDateTime(item.month)}
          </button>
        ))}
      </div>

      {/* Dashboard Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <div className=" p-5 xs:p-2  rounded-lg shadow-lg ">
          <div className="flex justify-between border-b p-4">
            {assessments.length > 0 ? (
              <div className=" p-5 rounded-lg shadow-md border border-gray-200 bg-blue-50">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  Assessment for {Utils.formatDateTime(currentRecord.month)}
                </h3>

                <p className="text-2xl font-bold text-blue-600 mt-2">
                  {currentOverallScore}
                </p>
              </div>
            ) : (
              <h3 className="text-red-500">No Assessments Available</h3>
            )}
          </div>

          <div className="divide-y text-sm">
            {assessmentCriteria.map((criterion, index) => {
              const value =
                currentRecord?.assessment?.[criterion] !== undefined
                  ? currentRecord.assessment[criterion]
                  : 0; // Default to 0 if assessment is missing

              const percentage = (value / 10) * 100; // Assuming 10 is the max score

              // Determine color based on percentage
              let barColor = "bg-red-400"; // Default red
              if (percentage >= 40 && percentage < 60)
                barColor = "bg-yellow-400";
              else if (percentage >= 60) barColor = "bg-green-400";

              return (
                <div
                  key={index}
                  className="py-3 px-4 flex items-center justify-between hover:bg-gray-50"
                >
                  <div className="flex-1">
                    <h4 className="text-gray-700">
                      {STUDENT_CRITERIA?.[criterion] || "Unknown Criteria"}
                    </h4>
                  </div>
                  <div className="flex flex-col items-end space-y-1 w-32">
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${barColor} transition-all duration-300`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-blue-50 p-5 xs:p-2 rounded-lg shadow-lg flex flex-col h-full">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <IoMdAnalytics /> Performance Analytics
          </h3>
          {/* <p className="text-gray-600 text-sm mb-4">
            Track your improvement over time.
          </p> */}

          {/* Chart should expand fully */}
          <div className="flex-1 flex items-center justify-center">
            <RadarChart assessment={currentRecord.assessment} />
          </div>
        </div>
      </div>
      <div className="p-5 xs:p-2  rounded-lg shadow-lg">
        {overallScore && (
          <LineChart
            assessment={overallScore}
            labels={Object.keys(overallScore)}
          />
        )}
      </div>
    </div>
  );
};

export default StudentDashboard;
